import type { MachinePark } from '../machine-park.model';

/** Canonical mock {@link MachinePark}. */
export const MOCK_MACHINE_PARK: MachinePark = {
  id: 1,
  name: '5-axis CNC mill',
  brand: 'DMG MORI',
  quantity: 2,
  datasheets: [],
  capability_ids: [11],
  machine_catalog_id: null,
  pending_catalog_review: false,
};

/** Builds a {@link MachinePark} from {@link MOCK_MACHINE_PARK}. */
export function createMockMachinePark(
  overrides?: Partial<MachinePark>
): MachinePark {
  return { ...MOCK_MACHINE_PARK, ...overrides };
}

/** Builds a list of {@link MachinePark} entries for seeding tests. */
export function createMockMachineParkList(
  count = 3,
  overrides?: Partial<MachinePark>
): MachinePark[] {
  return Array.from({ length: count }, (_, i) =>
    createMockMachinePark({
      id: i + 1,
      name: `Machine ${i + 1}`,
      ...overrides,
    })
  );
}

/**
 * AAE zone plant-layout equipment (factory id 43).
 * Names and quantities come from the AAE plant-layout board.
 * Ids start at 101 so they do not collide with showcase factory 1.
 */
export const MOCK_AAE_MACHINE_PARK: MachinePark[] = [
  createMockMachinePark({
    id: 101,
    name: 'Trumpf 7000 laser cutting machine — 6 m × 2.5 m',
    brand: 'Trumpf',
    quantity: 1,
    capability_ids: [111],
    machine_catalog_id: 3,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 102,
    name: 'Bystronic BySprint laser cutting machine — 4 m × 2 m',
    brand: 'Bystronic',
    quantity: 1,
    capability_ids: [111],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 103,
    name: 'Penta Fiber laser cutting machine — 6 m × 2.5 m, 6 kW',
    brand: 'Penta',
    quantity: 1,
    capability_ids: [111],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 104,
    name: 'Waterjet Co bevel-head waterjet — 6 m × 2 m, 60k PSI',
    brand: 'Waterjet Co',
    quantity: 1,
    capability_ids: [113],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 105,
    name: 'Fronius TPS 3201 MIG welder',
    brand: 'Fronius',
    quantity: 23,
    capability_ids: [41],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 106,
    name: 'ESAB 4000 MIG welder',
    brand: 'ESAB',
    quantity: 9,
    capability_ids: [41],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 107,
    name: 'Kemppi FastMig 450',
    brand: 'Kemppi',
    quantity: 5,
    capability_ids: [41],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 108,
    name: 'Kemppi MasterTig TIG welder',
    brand: 'Kemppi',
    quantity: 5,
    capability_ids: [42],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 109,
    name: 'Demmeler welding table',
    brand: 'Demmeler',
    quantity: 10,
    capability_ids: [41],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 110,
    name: 'Welding manipulator',
    brand: '',
    quantity: 8,
    capability_ids: [41, 141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 111,
    name: '10 t overhead crane',
    brand: '',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 112,
    name: '3 t overhead crane',
    brand: '',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 113,
    name: '21 t overhead crane',
    brand: '',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 114,
    name: '5 t diesel forklift',
    brand: '',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 115,
    name: '3 t electric forklift',
    brand: '',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 116,
    name: 'Faro CMM with laser probe — 2.7 m',
    brand: 'Faro',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 117,
    name: 'Faro Edge measuring scanner',
    brand: 'Faro',
    quantity: 1,
    capability_ids: [141],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 118,
    name: 'HAAS DS-30Y dual-spindle CNC lathe — 457 mm capacity',
    brand: 'HAAS',
    quantity: 1,
    capability_ids: [12],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 119,
    name: 'HAAS VF-4 CNC vertical machining center — 1270 × 508 × 635 mm',
    brand: 'HAAS',
    quantity: 1,
    capability_ids: [11, 211],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 120,
    name: 'Shotblasting booth',
    brand: '',
    quantity: 2,
    capability_ids: [121],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 121,
    name: 'Paint booth — 8 m × 6 m',
    brand: '',
    quantity: 9,
    capability_ids: [121],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 122,
    name: 'Ermaksan press brake — 8 m × 800 T',
    brand: 'Ermaksan',
    quantity: 1,
    capability_ids: [31],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 123,
    name: 'Trumpf TruBend 5320 press brake — 320 T',
    brand: 'Trumpf',
    quantity: 1,
    capability_ids: [31],
    machine_catalog_id: null,
    pending_catalog_review: false,
  }),
];

/**
 * Showcase factory (id 1) machine-park seeds shared by MSW handlers and the
 * government dashboard ownership aggregate — keep these in lockstep with
 * {@link import('@indutrust/factory-data').machineParkHandlers}.
 */
export const MOCK_SHOWCASE_MACHINE_PARK: MachinePark[] = [
  createMockMachinePark({
    id: 1,
    name: '5-axis CNC mill',
    brand: 'DMG MORI',
    quantity: 2,
    datasheets: [],
    machine_catalog_id: 1,
    pending_catalog_review: false,
  }),
  createMockMachinePark({
    id: 2,
    name: 'Legacy manual lathe',
    brand: 'Acme Tools',
    quantity: 1,
    datasheets: [],
    machine_catalog_id: null,
    pending_catalog_review: true,
  }),
];

/**
 * Demo parks that back `GET /dashboard/` machine-park ownership: showcase
 * factory 1 plus AAE factory 43. Empty-brand rows stay in the park list
 * (factory profile) but the ownership aggregator skips them.
 */
export const MOCK_DEMO_DASHBOARD_MACHINE_PARKS: readonly MachinePark[] = [
  ...MOCK_SHOWCASE_MACHINE_PARK,
  ...MOCK_AAE_MACHINE_PARK,
];
